import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import {
    VStack,
    HStack,
    Text,
    Textarea,
    Checkbox,
    Button,
    Badge,
    Box,
} from '@chakra-ui/react';
import { useAppStore } from '../../state/store';
import { useWorksharingStore } from '../../state/useWorksharingStore';
import { ManageDialogFrame, ManageDialogPrimaryButton } from '../manage/ManageDialogFrame';

/**
 * Synchronize with Central dialog (Collaborate > Synchronize).
 * BIM's "Synchronize Settings" page: compact central, comment, and the
 * relinquish options. "Synchronize Now" from the ribbon runs with the
 * remembered settings; this dialog performs the sync when OK is pressed.
 */
export function SynchronizeDialog() {
    const { t } = useTranslation();
    const isOpen = useAppStore((state) => state.collabDialog === 'synchronize');
    const onClose = useAppStore((state) => state.closeCollabDialog);

    const worksharingEnabled = useWorksharingStore((s) => s.worksharingEnabled);
    const centralPath = useWorksharingStore((s) => s.centralPath);
    const settings = useWorksharingStore((s) => s.syncSettings);
    const updateSyncSettings = useWorksharingStore((s) => s.updateSyncSettings);
    const synchronizeWithCentral = useWorksharingStore((s) => s.synchronizeWithCentral);

    const [comment, setComment] = useState('');
    const [busy, setBusy] = useState(false);

    const handleSync = async () => {
        setBusy(true);
        const ok = await synchronizeWithCentral(comment, settings.relinquishAfterSync);
        setBusy(false);
        if (ok) onClose();
    };

    return (
        <ManageDialogFrame
            title={t('collab.synchronizeWithCentral')}
            isOpen={isOpen}
            onClose={onClose}
            footer={
                <HStack spacing={2}>
                    <ManageDialogPrimaryButton
                        label={busy ? 'Synchronizing...' : 'Synchronize'}
                        onClick={handleSync}
                        isDisabled={busy || !worksharingEnabled}
                    />
                    <Button size="sm" variant="ghost" onClick={onClose}>{t('collab.cancel')}</Button>
                </HStack>
            }
        >
            <VStack spacing={4} align="stretch">
                {!worksharingEnabled ? (
                    <Text fontSize="sm" color="var(--color-text-secondary)">{t('collab.thisModelIsNotSetUpForWorksharingYetStartCollaborationWithTheColla')}</Text>
                ) : (
                    <>
                        <HStack>
                            <Text fontSize="xs" color="var(--color-text-secondary)" flexShrink={0}>{t('collab.centralModel')}</Text>
                            <Text fontSize="xs" isTruncated>{centralPath}</Text>
                            <Badge colorScheme="green" flexShrink={0}>{t('collab.connected')}</Badge>
                        </HStack>

                        <Box>
                            <Text fontSize="xs" fontWeight="semibold" mb={1}>{t('collab.commentSavedWithThisSynchronization')}</Text>
                            <Textarea
                                size="sm"
                                rows={3}
                                placeholder={t('collab.describeWhatYouChanged')}
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                bg="var(--color-bg-primary)"
                                borderColor="var(--color-border)"
                            />
                        </Box>

                        <VStack align="stretch" spacing={2}>
                            <Text fontSize="xs" fontWeight="semibold">{t('collab.synchronizeRelinquishOptions')}</Text>
                            <Checkbox
                                size="sm"
                                isChecked={settings.compactCentral}
                                onChange={(e) => updateSyncSettings({ compactCentral: e.target.checked })}
                            >
                                <Text fontSize="sm">{t('collab.compactCentral')}</Text>
                            </Checkbox>
                            <Checkbox
                                size="sm"
                                isChecked={settings.relinquishAfterSync}
                                onChange={(e) => updateSyncSettings({ relinquishAfterSync: e.target.checked })}
                            >
                                <Text fontSize="sm">{t('collab.relinquishAllMineAfterSynchronizing')}</Text>
                            </Checkbox>
                        </VStack>
                    </>
                )}
            </VStack>
        </ManageDialogFrame>
    );
}
